import { createContext, useContext, useEffect, useState } from "react";

// ─── Translation files ────────────────────────────────────────────
const en = {
  // Nav
  nav_devices: "Devices",
  nav_get_app: "Get the app",
  nav_pair_code: "Pair by code",
  nav_billing: "Billing & quotas",
  nav_users: "Users",
  nav_plans: "Plans & packs",
  nav_subscriptions: "Subscriptions",
  nav_releases: "Releases",
  nav_settings: "Settings",
  nav_audit: "Audit log",
  nav_templates: "Notification templates",
  nav_signout: "Sign out",

  // Auth
  auth_sign_in: "Sign in",
  auth_welcome_back: "Welcome back",
  auth_email: "Email",
  auth_password: "Password",
  auth_authenticating: "Authenticating…",
  auth_change_server: "Change server",

  // Setup
  setup_installation: "Installation",
  setup_first_run: "First-run setup.",
  setup_description: "A few configuration steps before your control plane is ready.",
  setup_steps_ready: "ready",
  setup_step: "step",

  // Devices
  devices_fleet: "Fleet",
  devices_title: "Devices",
  devices_online: "online",
  devices_total: "Total",
  devices_search: "search by name, hostname, id…",
  devices_no_devices: "no devices yet",
  devices_no_devices_sub: "install the agent on a machine to see it appear here",
  devices_connect: "Connect",
  devices_last_seen: "last seen",
  devices_ago: "ago",

  // Billing
  billing_account: "Account",
  billing_title: "Billing & quotas",
  billing_current_plan: "Current plan",
  billing_your_usage: "Your usage",
  billing_device_slots: "Device slots",
  billing_viewer_slots: "Viewer slots",
  billing_parallel_viewers: "parallel viewers",
  billing_base_plan: "base plan",
  billing_from_purchases: "from purchases",
  billing_subscriptions: "Subscriptions",
  billing_provider: "Provider",
  billing_interval: "Interval",
  billing_status: "Status",
  billing_renews: "Renews",
  billing_cancel: "Cancel",
  billing_plans: "Plans",
  billing_no_provider: "No payment provider is enabled. An admin needs to configure Stripe or PayPal.",
  billing_slot_packs: "Slot packs",
  billing_slot_packs_sub: "One-time purchases that permanently add slots to your account.",
  billing_purchase_history: "Purchase history",
  billing_per_month: "per month",
  billing_per_year: "per year",
  billing_pay_stripe: "Pay with Stripe",
  billing_pay_paypal: "Pay with PayPal",
  billing_buy_stripe: "Buy with Stripe",
  billing_buy_paypal: "Buy with PayPal",
  billing_cancel_confirm: "Cancel this subscription? Your plan will be downgraded immediately and any excess devices will be disabled.",
  billing_canceled: "Subscription canceled.",

  // Downloads
  downloads_get_app: "Get the app",
  downloads_title: "Download Vorks Connect",
  downloads_subtitle: "Install on any device you want to access or share",
  downloads_no_releases: "no releases available yet",
  downloads_no_releases_sub: "an admin needs to upload installer binaries first",
  downloads_verify: "Verify checksum",
  downloads_download: "Download installer",
  downloads_preparing: "Preparing…",
  downloads_after_title: "After installing",
  downloads_after_1: "Launch Vorks Connect from your start menu / applications.",
  downloads_after_2: "Enter the backend URL:",
  downloads_after_3: "Click \"Sign in with browser\". Your browser will open this site and ask you to approve the device.",
  downloads_after_4: "Choose whether you want this machine to act as a host, viewer, or both.",

  // Admin – users
  admin_users_title: "Users",
  admin_users_accounts: "accounts",
  admin_users_plan: "Plan",
  admin_users_devices: "Devices",
  admin_users_effective_slots: "Effective slots",
  admin_users_last_login: "Last login",
  admin_users_never: "never",
  admin_users_edit: "Edit",
  admin_users_warn: "Warn",
  admin_users_suspend: "Suspend",
  admin_users_unsuspend: "Unsuspend",
  admin_users_warn_placeholder: "Reason for warning…",
  admin_users_suspend_placeholder: "Reason for suspension…",
  admin_users_updated: "User updated.",
  admin_users_warned: "Warning issued.",
  admin_users_suspended: "User suspended.",
  admin_users_unsuspended: "User unsuspended.",

  // Admin – plans
  admin_plans_title: "Plans & packs",
  admin_plans_sync_stripe: "Sync to Stripe",
  admin_plans_sync_paypal: "Sync to PayPal",
  admin_plans_syncing: "Syncing…",
  admin_plans_new_plan: "+ New plan",
  admin_plans_new_pack: "+ New pack",
  admin_plans_subscription_plans: "Subscription plans",
  admin_plans_slot_packs: "Slot packs (one-time)",

  // Admin – settings
  admin_settings_title: "Settings",
  admin_settings_payment_restricted: "Payment settings are restricted to Superadmin.",

  // Admin – audit
  admin_audit_title: "Audit log",
  admin_audit_search: "filter by action, user, IP…",
  admin_audit_no_entries: "no entries match",

  // Admin – releases
  admin_releases_title: "Client releases",
  admin_releases_upload: "Upload new release",
  admin_releases_make_current: "Make current after upload",
  admin_releases_uploading: "Uploading…",
  admin_releases_all: "All releases",
  admin_releases_no_releases: "no releases uploaded yet",
  admin_releases_deleted: "Deleted.",
  admin_releases_current: "Set as current.",

  // Templates
  tpl_title: "Notification templates",
  tpl_subtitle: "Customize the messages sent for each event",
  tpl_select_event: "Select an event",
  tpl_label: "Label",
  tpl_description: "Description",
  tpl_mail: "Mail",
  tpl_discord: "Discord",
  tpl_mail_enabled: "Send email for this event",
  tpl_discord_enabled: "Send Discord notification for this event",
  tpl_subject: "Subject",
  tpl_html_body: "HTML body",
  tpl_text_body: "Plain-text fallback",
  tpl_discord_title: "Embed title",
  tpl_discord_description: "Embed description",
  tpl_discord_color: "Accent color",
  tpl_available_vars: "Available variables",
  tpl_vars_hint: "Click a variable to insert it at the cursor.",
  tpl_preview: "Live preview",
  tpl_preview_mail: "Mail preview",
  tpl_preview_discord: "Discord preview",
  tpl_save: "Save template",
  tpl_send_test: "Send test mail",
  tpl_saved: "Template saved.",
  tpl_test_sent: "Test mail sent to your address.",

  // Roles
  role_superadmin: "Superadmin",
  role_admin: "Admin",
  role_moderator: "Moderator",
  role_user: "User",

  // Common
  common_save: "Save changes",
  common_cancel: "Cancel",
  common_loading: "Loading",
  common_error: "Error",
  common_ok: "OK",
  common_yes: "Yes",
  common_no: "No",
  common_search: "Search",
  common_delete: "Delete",
  common_edit: "Edit",
  common_active: "active",
  common_inactive: "inactive",
  common_never: "never",
  common_synced: "synced",
  common_archived: "archived",
  lang_switch: "Deutsch",
};

const de = {
  // Nav
  nav_devices: "Geräte",
  nav_get_app: "App herunterladen",
  nav_pair_code: "Per Code koppeln",
  nav_billing: "Abrechnung & Kontingente",
  nav_users: "Benutzer",
  nav_plans: "Pläne & Pakete",
  nav_subscriptions: "Abonnements",
  nav_releases: "Versionen",
  nav_settings: "Einstellungen",
  nav_audit: "Audit-Log",
  nav_templates: "Benachrichtigungs-Templates",
  nav_signout: "Abmelden",

  // Auth
  auth_sign_in: "Anmelden",
  auth_welcome_back: "Willkommen zurück",
  auth_email: "E-Mail",
  auth_password: "Passwort",
  auth_authenticating: "Authentifizierung…",
  auth_change_server: "Server wechseln",

  // Setup
  setup_installation: "Installation",
  setup_first_run: "Ersteinrichtung.",
  setup_description: "Einige Konfigurationsschritte, bevor die Steuerungsebene bereit ist.",
  setup_steps_ready: "bereit",
  setup_step: "Schritt",

  // Devices
  devices_fleet: "Gerätepark",
  devices_title: "Geräte",
  devices_online: "online",
  devices_total: "Gesamt",
  devices_search: "Suche nach Name, Hostname, ID…",
  devices_no_devices: "noch keine Geräte",
  devices_no_devices_sub: "Installiere den Agenten auf einem Gerät, damit es hier erscheint",
  devices_connect: "Verbinden",
  devices_last_seen: "zuletzt gesehen",
  devices_ago: "vor",

  // Billing
  billing_account: "Konto",
  billing_title: "Abrechnung & Kontingente",
  billing_current_plan: "Aktueller Plan",
  billing_your_usage: "Deine Nutzung",
  billing_device_slots: "Geräteplätze",
  billing_viewer_slots: "Viewer-Plätze",
  billing_parallel_viewers: "parallele Viewer",
  billing_base_plan: "Basisplan",
  billing_from_purchases: "durch Käufe",
  billing_subscriptions: "Abonnements",
  billing_provider: "Anbieter",
  billing_interval: "Intervall",
  billing_status: "Status",
  billing_renews: "Verlängert",
  billing_cancel: "Kündigen",
  billing_plans: "Pläne",
  billing_no_provider: "Kein Zahlungsanbieter aktiv. Ein Admin muss Stripe oder PayPal konfigurieren.",
  billing_slot_packs: "Slot-Pakete",
  billing_slot_packs_sub: "Einmalige Käufe, die deinem Konto dauerhaft Plätze hinzufügen.",
  billing_purchase_history: "Kaufhistorie",
  billing_per_month: "pro Monat",
  billing_per_year: "pro Jahr",
  billing_pay_stripe: "Mit Stripe zahlen",
  billing_pay_paypal: "Mit PayPal zahlen",
  billing_buy_stripe: "Mit Stripe kaufen",
  billing_buy_paypal: "Mit PayPal kaufen",
  billing_cancel_confirm: "Dieses Abonnement kündigen? Dein Plan wird sofort herabgestuft und überzählige Geräte werden deaktiviert.",
  billing_canceled: "Abonnement gekündigt.",

  // Downloads
  downloads_get_app: "App herunterladen",
  downloads_title: "Vorks Connect herunterladen",
  downloads_subtitle: "Auf jedem Gerät installieren, auf das du zugreifen oder das du freigeben möchtest",
  downloads_no_releases: "noch keine Versionen verfügbar",
  downloads_no_releases_sub: "Ein Admin muss zuerst Installer-Dateien hochladen",
  downloads_verify: "Prüfsumme verifizieren",
  downloads_download: "Installer herunterladen",
  downloads_preparing: "Wird vorbereitet…",
  downloads_after_title: "Nach der Installation",
  downloads_after_1: "Starte Vorks Connect aus dem Startmenü / den Anwendungen.",
  downloads_after_2: "Gib die Backend-URL ein:",
  downloads_after_3: "Klicke auf 'Mit Browser anmelden'. Dein Browser öffnet diese Seite und bittet dich, das Gerät zu bestätigen.",
  downloads_after_4: "Wähle, ob dieses Gerät als Host, Viewer oder beides fungieren soll.",

  // Admin – users
  admin_users_title: "Benutzer",
  admin_users_accounts: "Konten",
  admin_users_plan: "Plan",
  admin_users_devices: "Geräte",
  admin_users_effective_slots: "Effektive Plätze",
  admin_users_last_login: "Letzter Login",
  admin_users_never: "nie",
  admin_users_edit: "Bearbeiten",
  admin_users_warn: "Warnen",
  admin_users_suspend: "Sperren",
  admin_users_unsuspend: "Entsperren",
  admin_users_warn_placeholder: "Grund für die Verwarnung…",
  admin_users_suspend_placeholder: "Grund für die Sperrung…",
  admin_users_updated: "Benutzer aktualisiert.",
  admin_users_warned: "Verwarnung ausgestellt.",
  admin_users_suspended: "Benutzer gesperrt.",
  admin_users_unsuspended: "Benutzer entsperrt.",

  // Admin – plans
  admin_plans_title: "Pläne & Pakete",
  admin_plans_sync_stripe: "Mit Stripe synchronisieren",
  admin_plans_sync_paypal: "Mit PayPal synchronisieren",
  admin_plans_syncing: "Synchronisierung…",
  admin_plans_new_plan: "+ Neuer Plan",
  admin_plans_new_pack: "+ Neues Paket",
  admin_plans_subscription_plans: "Abonnementpläne",
  admin_plans_slot_packs: "Slot-Pakete (einmalig)",

  // Admin – settings
  admin_settings_title: "Einstellungen",
  admin_settings_payment_restricted: "Zahlungseinstellungen sind Superadmins vorbehalten.",

  // Admin – audit
  admin_audit_title: "Audit-Log",
  admin_audit_search: "Nach Aktion, Benutzer, IP filtern…",
  admin_audit_no_entries: "keine Einträge gefunden",

  // Admin – releases
  admin_releases_title: "Client-Versionen",
  admin_releases_upload: "Neue Version hochladen",
  admin_releases_make_current: "Nach dem Upload als aktuell markieren",
  admin_releases_uploading: "Wird hochgeladen…",
  admin_releases_all: "Alle Versionen",
  admin_releases_no_releases: "noch keine Versionen hochgeladen",
  admin_releases_deleted: "Gelöscht.",
  admin_releases_current: "Als aktuell gesetzt.",

  // Templates
  tpl_title: "Benachrichtigungs-Templates",
  tpl_subtitle: "Passe die Nachrichten für jedes Event individuell an",
  tpl_select_event: "Event wählen",
  tpl_label: "Bezeichnung",
  tpl_description: "Beschreibung",
  tpl_mail: "E-Mail",
  tpl_discord: "Discord",
  tpl_mail_enabled: "E-Mail für dieses Event senden",
  tpl_discord_enabled: "Discord-Nachricht für dieses Event senden",
  tpl_subject: "Betreff",
  tpl_html_body: "HTML-Inhalt",
  tpl_text_body: "Plain-Text-Fallback",
  tpl_discord_title: "Embed-Titel",
  tpl_discord_description: "Embed-Beschreibung",
  tpl_discord_color: "Akzentfarbe",
  tpl_available_vars: "Verfügbare Variablen",
  tpl_vars_hint: "Klick auf eine Variable, um sie an der Cursor-Position einzufügen.",
  tpl_preview: "Live-Vorschau",
  tpl_preview_mail: "E-Mail-Vorschau",
  tpl_preview_discord: "Discord-Vorschau",
  tpl_save: "Template speichern",
  tpl_send_test: "Test-Mail senden",
  tpl_saved: "Template gespeichert.",
  tpl_test_sent: "Test-Mail an deine Adresse gesendet.",

  // Roles
  role_superadmin: "Superadmin",
  role_admin: "Admin",
  role_moderator: "Moderator",
  role_user: "Benutzer",

  // Common
  common_save: "Änderungen speichern",
  common_cancel: "Abbrechen",
  common_loading: "Laden",
  common_error: "Fehler",
  common_ok: "OK",
  common_yes: "Ja",
  common_no: "Nein",
  common_search: "Suchen",
  common_delete: "Löschen",
  common_edit: "Bearbeiten",
  common_active: "aktiv",
  common_inactive: "inaktiv",
  common_never: "nie",
  common_synced: "synchronisiert",
  common_archived: "archiviert",
  lang_switch: "English",
};

// ─── Context ─────────────────────────────────────────────────────
const I18nCtx = createContext(null);
const STORAGE_KEY = "vorks.lang";

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "de" || stored === "en") return stored;
    return navigator.language?.startsWith("de") ? "de" : "en";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const strings = lang === "de" ? de : en;

  function t(key, ...args) {
    let s = strings[key] || en[key] || key;
    // Simple interpolation: {0}, {1}, etc.
    args.forEach((a, i) => { s = s.replace(`{${i}}`, a); });
    return s;
  }

  function toggleLang() {
    setLang((l) => (l === "en" ? "de" : "en"));
  }

  return (
    <I18nCtx.Provider value={{ lang, t, toggleLang }}>
      {children}
    </I18nCtx.Provider>
  );
}

export function useT() {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error("useT must be inside I18nProvider");
  return ctx;
}
